/* eslint-disable no-unreachable */
import pick from 'lodash/pick';
import fetch from '../utils/fetch';
import CustomError from '../utils/CustomError';
import {
  notificationsEndpoint,
  updateUserEndPointV3,
  notificationConfigV4
} from '../constants/endpoints';
import {
  UNEXPECTED_ERROR,
  GET_NOTIFICATIONS_SUCCESS,
  UPDATE_NOTIFICATIONS_SUCCESS,
  TRANSACTION_ID
} from '../constants/services';

/**
 * getNotificationServ function to get notifications settings
 * * @param {string} token is a valid auth token for the endpoints
 * * @returns {object} returns endpoint response or throw endpoint error
 * */
export async function getNotificationServ(token, bundleAppId) {
  try {
    const query = await fetch(
      `${notificationsEndpoint}/apps/${bundleAppId}/notifications`,
      {
        method: 'get',
        token
      }
    );
    const transactionId = query.headers.get(TRANSACTION_ID);
    const json = await query.json();
    if (json.error) {
      throw new CustomError({ ...json, transactionId });
    }
    const res = {
      status: GET_NOTIFICATIONS_SUCCESS,
      data: json.data
    };
    return res;
  } catch (error) {
    if (error.isCustomError) {
      throw error;
    }
    throw new CustomError(UNEXPECTED_ERROR);
  }
}

/**
 * updateNotificationServ function to set notifications settings
 * * @param {string} token is a valid auth token for the endpoints
 * * * @param {object} data object with notification settings to save
 * * @returns {object} returns endpoint response or throw endpoint error
 * */
export async function updateNotificationServ(token, data, bundleAppId) {
  try {
    const body = pick(data, ['channels', 'subscriptions']);
    const query = await fetch(
      `${notificationsEndpoint}/apps/${bundleAppId}/notifications`,
      {
        method: 'put',
        token,
        body: JSON.stringify(body)
      }
    );
    const transactionId = query.headers.get(TRANSACTION_ID);
    const json = await query.json();
    if (json.error) {
      throw new CustomError({ ...json, transactionId });
    }
    const res = {
      status: UPDATE_NOTIFICATIONS_SUCCESS,
      data: json.data.notifications
    };
    return res;
  } catch (error) {
    if (error.isCustomError) {
      throw error;
    }
    throw new CustomError(UNEXPECTED_ERROR);
  }
}

/**
 * updateTokenServ function to store notification token
 * * @param {string} token is a valid auth token for the endpoints
 * * * @param {object} data object with notification settings to save
 * * @returns {object} returns endpoint response or throw endpoint error
 * */
export async function updateTokenServ(token, data) {
  try {
    const query = await fetch(updateUserEndPointV3, {
      method: 'put',
      token,
      headers: {
        'Content-Type': 'application/json; charset=utf-8'
      },
      body: JSON.stringify(data)
    });
    const transactionId = query.headers.get(TRANSACTION_ID);
    const json = await query.json();
    if (json.error) {
      throw new CustomError({ ...json, transactionId });
    }
    const res = {
      status: UPDATE_NOTIFICATIONS_SUCCESS,
      data: json.data
    };
    return res;
  } catch (error) {
    if (error.isCustomError) {
      throw error;
    }
    throw new CustomError(UNEXPECTED_ERROR);
  }
}

/**
 * setUserDevice function to add user device
 * * @param {object} data data for add user device
 * * @returns {object} returns endpoint response or throw endpoint error
 * */
export async function setUserDeviceServ(token, data) {
  try {
    const url = `${notificationsEndpoint}/apps/${data?.urlParams?.bundleAppId}/notifications/${data?.urlParams?.exponentPushToken}`;
    const query = await fetch(url, {
      method: 'put',
      token,
      body: JSON.stringify(data?.bodyParams)
    });
    const transactionId = query.headers.get(TRANSACTION_ID);
    const json = await query.json();

    if (json.error) {
      throw new CustomError({ ...json, transactionId });
    }
    const res = {
      status: GET_NOTIFICATIONS_SUCCESS,
      data: json.data
    };
    return res;
  } catch (error) {
    if (error.isCustomError) {
      throw error;
    }
    throw new CustomError(UNEXPECTED_ERROR);
  }
}

export async function getTurbineAlarmEventsServ(token, eventCode, bundleAppId) {
  try {
    const params = new URLSearchParams({
      eventCode,
      bundleAppId,
      active: 'true',
      withTemplates: 'false'
    });
    const query = await fetch(
      `${notificationConfigV4}/events/turbines?${params.toString()}`,
      {
        method: 'get',
        token
      }
    );
    const transactionId = query.headers.get(TRANSACTION_ID);
    const json = await query.json();
    if (json.error) {
      throw new CustomError({ ...json, transactionId });
    }
    const res = {
      status: GET_NOTIFICATIONS_SUCCESS,
      data: json.data
    };
    return res;
  } catch (error) {
    if (error.isCustomError) {
      throw error;
    }
    throw new CustomError(UNEXPECTED_ERROR);
  }
}

export async function getTurbineAlarmSubscriptionsServ(
  token,
  eventCode,
  bundleAppId,
  eventName
) {
  try {
    const params = new URLSearchParams({
      eventCode,
      bundleAppId,
      eventName
    });

    let url = '';

    if (eventName) {
      url = `${notificationConfigV4}/subscriptions/turbines/list?${params.toString()}&eventName=${eventName}`;
    } else {
      url = `${notificationConfigV4}/subscriptions/turbines/list?${params.toString()}`;
    }

    const query = await fetch(url, {
      method: 'get',
      token
    });
    const transactionId = query.headers.get(TRANSACTION_ID);
    const json = await query.json();
    if (json.error) {
      throw new CustomError({ ...json, transactionId });
    }
    const res = {
      status: GET_NOTIFICATIONS_SUCCESS,
      data: json.data
    };
    return res;
  } catch (error) {
    if (error.isCustomError) {
      throw error;
    }
    throw new CustomError(UNEXPECTED_ERROR);
  }
}

export async function updateTurbineAlarmSubscriptionsServ(
  token,
  bundleAppId,
  eventCode,
  eventNames
) {
  try {
    const body = {
      bundleAppId,
      eventCode,
      eventNames
    };
    const query = await fetch(`${notificationConfigV4}/subscriptions`, {
      method: 'patch',
      token,
      body: JSON.stringify(body)
    });
    const transactionId = query.headers.get(TRANSACTION_ID);
    const json = await query.json();
    if (json.error) {
      throw new CustomError({ ...json, transactionId });
    }
    const res = {
      status: UPDATE_NOTIFICATIONS_SUCCESS,
      data: json.data
    };
    return res;
  } catch (error) {
    if (error.isCustomError) {
      throw error;
    }
    throw new CustomError(UNEXPECTED_ERROR);
  }
}

export async function postTurbineAlarmSubscriptionsServ(
  token,
  bundleAppId,
  eventCode,
  eventName
) {
  try {
    const body = {
      bundleAppId,
      eventCode,
      eventName
    };
    const query = await fetch(`${notificationConfigV4}/subscriptions`, {
      method: 'post',
      token,
      body: JSON.stringify(body)
    });
    const transactionId = query.headers.get(TRANSACTION_ID);
    const json = await query.json();
    if (json.error) {
      throw new CustomError({ ...json, transactionId });
    }
    const res = {
      status: UPDATE_NOTIFICATIONS_SUCCESS,
      data: json.data
    };
    return res;
  } catch (error) {
    if (error.isCustomError) {
      throw error;
    }
    throw new CustomError(UNEXPECTED_ERROR);
  }
}

export async function updateTurbineAlarmSubscriptionsOperationServ(token, id) {
  try {
    const query = await fetch(`${notificationConfigV4}/subscriptions/${id}`, {
      method: 'delete',
      token
    });
    const transactionId = query.headers.get(TRANSACTION_ID);
    const json = await query.json();
    if (json.error) {
      throw new CustomError({ ...json, transactionId });
    }
    const res = {
      status: UPDATE_NOTIFICATIONS_SUCCESS,
      data: json.data
    };
    return res;
  } catch (error) {
    if (error.isCustomError) {
      throw error;
    }
    throw new CustomError(UNEXPECTED_ERROR);
  }
}

export default {
  getNotificationServ,
  updateNotificationServ,
  updateTokenServ,
  setUserDeviceServ,
  getTurbineAlarmEventsServ,
  getTurbineAlarmSubscriptionsServ,
  updateTurbineAlarmSubscriptionsServ
};
